"use client"

import { useMemo, useState, useSyncExternalStore } from "react"
import { useImageLoadFailure } from "@/components/ui/use-image-load-failure"
import { SearchInput } from "./search-input"
import { orderBrandsForGrid } from "@/lib/brands/grid-order"
import { optimizedImgSrc } from "@/lib/image-optimizable"

// These breakpoints match the standard grid's sm:grid-cols-3/md:grid-cols-5.
function subscribeColumns(notify: () => void) {
  const queries = [window.matchMedia('(min-width: 640px)'), window.matchMedia('(min-width: 768px)')]
  queries.forEach(query => query.addEventListener('change', notify))
  return () => queries.forEach(query => query.removeEventListener('change', notify))
}
function viewportColumns() {
  return window.matchMedia('(min-width: 768px)').matches ? 5 : window.matchMedia('(min-width: 640px)').matches ? 3 : 2
}

/** Show a prefix of real brands, completing a row unless the set is exhausted. */
export function completeBrandRows(requested: number, total: number, columns: number): number {
  return Math.min(total, Math.ceil(requested / columns) * columns)
}

interface Brand {
  /** Explicit false retains the brand identity without a gated destination. */
  linkable?: boolean
  name: string
  slug: string
  /** Logo image URL. If null/empty, the card renders the brand name as text. */
  logo: string | null
}

// canonicalBrandKey, the popularity rank and the grid order live in lib/brands/grid-order.ts
// (FRIDGE-1623), so the server picks the first tiles in the same order.

interface BrandLogoGridProps {
  /** All brands to display */
  brands: Brand[]
  /** Number of brands to show initially before "Show More" (defaults to 10) */
  initialCount?: number
  /**
   * When set, "Show More" reveals this many additional brands per click instead of
   * toggling fully expanded. Set to 0 or omit for legacy toggle behavior.
   */
  expandStep?: number
  /** Complete responsive rows while more matching brands remain. */
  completeRows?: boolean
  /** Show a search input above the grid to filter by brand name */
  searchable?: boolean
  /** Search input placeholder (only used when searchable) */
  searchPlaceholder?: string
  /** Section title */
  title?: string
  /** Section description */
  description?: string
  /** Additional classes on the wrapper */
  className?: string
  /**
   * Compact mode for narrow contexts (e.g. blog sidebar). Locks the grid to
   * 2 columns regardless of viewport, shrinks the heading + logo height, and
   * uses square cards. Default 5-col responsive layout is unchanged.
   */
  compact?: boolean
  /**
   * FRIDGE-1623: how many brands exist when `brands` holds only the first
   * tiles (a product page sends the ten it shows, not all of them). With
   * `onNeedAll`, "Show More" and the search ask the parent for the rest.
   */
  totalCount?: number
  /** Asks the parent for every brand; it passes them back through `brands`. */
  onNeedAll?: () => void
}

export function BrandLogoGrid({
  brands,
  initialCount = 10,
  expandStep = 0,
  completeRows = false,
  searchable = false,
  searchPlaceholder = "Search brands...",
  title = "Shop by Manufacturer",
  description = "The world's leading cooling technology, all in one marketplace.",
  className = "",
  compact = false,
  totalCount,
  onNeedAll,
}: BrandLogoGridProps) {
  const [expanded, setExpanded] = useState(false)
  const [visibleCount, setVisibleCount] = useState(initialCount)
  const [query, setQuery] = useState("")
  const viewportColumnCount = useSyncExternalStore(subscribeColumns, viewportColumns, () => 2)
  const columns = compact ? 2 : viewportColumnCount

  // Dedup by canonical brand (one GE, one Summit…) then order by owned-data US
  // popularity — most popular first, unranked brands alphabetically after the head.
  const orderedBrands = useMemo(() => orderBrandsForGrid(brands), [brands])

  const filteredBrands = useMemo(() => {
    if (!searchable || !query.trim()) return orderedBrands
    const q = query.trim().toLowerCase()
    return orderedBrands.filter((b) => b.name.toLowerCase().includes(q))
  }, [orderedBrands, query, searchable])

  // Incremental mode reveals `expandStep` more each click; legacy mode toggles all.
  const incremental = expandStep > 0

  const requestedCount = incremental ? visibleCount : initialCount
  const revealCount = completeRows
    ? completeBrandRows(requestedCount, filteredBrands.length, columns)
    : requestedCount

  // When searching, show all matches so the user sees every result for their query.
  const visibleBrands = query.trim()
    ? filteredBrands
    : incremental
      ? filteredBrands.slice(0, revealCount)
      : expanded
        ? filteredBrands
        : filteredBrands.slice(0, revealCount)

  // FRIDGE-1623: only the first tiles are here and the rest exist, so there is more to show.
  const partial = !!onNeedAll && typeof totalCount === "number" && totalCount > orderedBrands.length
  const hasMore = !query.trim() && (
    incremental
      ? filteredBrands.length > revealCount || partial
      : !expanded && (filteredBrands.length > revealCount || partial)
  )
  const canCollapse = !query.trim() && (
    incremental
      ? visibleCount > initialCount
      : expanded
  )

  function handleToggle() {
    if (partial && hasMore) onNeedAll?.()
    if (incremental) {
      if (hasMore) {
        setVisibleCount(Math.min(revealCount + expandStep, filteredBrands.length))
      } else {
        setVisibleCount(initialCount)
      }
    } else {
      setExpanded((e) => !e)
    }
  }

  // Compact layout swaps the section chrome and grid for a sidebar-friendly
  // variant: locked 2-col grid, square cards, smaller heading, smaller logos.
  // Standard layout (default) is unchanged for production hub/collection use.
  const sectionClass = compact
    ? `mt-2 ${className}`
    : `mt-16 pt-12 border-t border-gray-200 ${className}`
  const gridClass = compact
    ? "grid grid-cols-2 gap-2"
    : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3"
  // FRIDGE-686 (owner circles on every card of the PDP grid, 08/31/2026:
  // "circles show way too much padding"): the non-compact card was
  // aspect-video (~163px tall at PDP width) around an h-12 logo — ~57px of
  // dead space above AND below every mark. Fixed height sized to the logo
  // plus honest breathing room; compact (sidebar strips) was already tight
  // and is untouched.
  const cardClass = compact
    ? "bg-white p-1.5 flex items-center justify-center rounded-lg border border-gray-200 hover:border-[#ff9951]/50 hover:shadow-md transition-all group cursor-pointer aspect-square"
    : "bg-white px-3 py-4 h-24 flex items-center justify-center rounded-xl border border-gray-200 hover:border-[#ff9951]/50 hover:shadow-md transition-all group cursor-pointer"
  // Logos ALWAYS render in full color (owner 07/19/2026: no grayscale, no
  // hover-only color reveal — brand marks read at full color at rest).
  // Uniform content box: fixed height fills every card to the same visual
  // weight, width capped so ultra-wide wordmarks don't dominate; object-contain
  // keeps every logo's aspect intact and centered (owner 07/19/2026: the Café /
  // F&P / Marvel logos rendered tiny against wide wordmarks).
  const logoClass = compact
    ? "h-9 max-w-[80%] object-contain transition-transform group-hover:scale-105"
    : "h-14 max-w-[82%] object-contain transition-transform group-hover:scale-105"
  const headingClass = compact
    ? "text-[11px] font-bold tracking-[0.18em] uppercase text-[#0469f0] mb-3"
    : "text-2xl font-black tracking-tight text-gray-900 mb-1"

  return (
    <section className={sectionClass}>
      <div className={compact ? "mb-3" : "mb-6"}>
        <h2 className={headingClass}>{title}</h2>
        {!compact && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-6">
            {description && (
              <p className="text-sm text-gray-500 max-w-2xl">{description}</p>
            )}
            {searchable && (
              <SearchInput
                name="brand_search"
                value={query}
                onChange={setQuery}
                onFocus={partial ? onNeedAll : undefined}
                placeholder={searchPlaceholder}
                className="w-full md:w-72 md:shrink-0 md:ml-auto"
              />
            )}
          </div>
        )}
      </div>
      {visibleBrands.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">No brands match &quot;{query}&quot;.</p>
      ) : (
        <div className={gridClass}>
          {visibleBrands.map((brand) => {
            const Tile = brand.linkable === false ? 'div' : 'a'
            return (
              <Tile
                key={brand.slug}
                href={brand.linkable === false ? undefined : `/brands/${brand.slug}`}
                className={brand.linkable === false ? cardClass.replace('cursor-pointer', 'cursor-default').replace(/hover:\S+|\bgroup\b/g, '') : cardClass}
              >
                <BrandTileContent tile={brand} logoClass={logoClass} compact={compact} />
              </Tile>
            )
          })}
        </div>
      )}
      {(hasMore || canCollapse) && (
        <div className={compact ? "mt-3 flex justify-center" : "mt-6 flex justify-center"}>
          <button
            onClick={handleToggle}
            className={`flex items-center gap-1.5 text-[#ff9951] font-bold hover:underline transition-all ${compact ? "text-xs" : "text-sm"}`}
          >
            <span>{hasMore ? (compact ? "Show more" : "Show More Manufacturers") : "Show Fewer"}</span>
            <svg
              className={`w-4 h-4 transition-transform ${!hasMore ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      )}
    </section>
  )
}

/**
 * What a manufacturer tile shows. The logo is the brand's STORED mark (our
 * brand-logos bucket, proxied path — lib/collection-page-data.ts
 * selectAvailableBrands hands in null for anything else) and the brand name
 * is the honest fallback: when there is no stored logo, or when the stored
 * bytes fail to load in the browser, the tile prints the name. Never a
 * broken-image icon, never an invented mark (FRIDGE-1052, the owner's
 * "where is adt" screenshot).
 */
export function BrandTileContent({
  tile,
  logoClass,
  compact,
  logoHeight = compact ? 36 : 56,
  logoWidth = compact ? '80%' : '82%',
}: {
  tile: Brand
  logoClass: string
  compact: boolean
  /** The logo's height in px, the same as logoClass's h-* class (FRIDGE-1540). */
  logoHeight?: number
  /** The logo's box width, the same as logoClass's width cap (FRIDGE-1540). */
  logoWidth?: number | string
}) {
  const { ref, failed: logoFailed } = useImageLoadFailure(tile.logo)
  const brand: Brand = logoFailed ? { ...tile, logo: null } : tile
  return brand.logo ? (
    // `loading="lazy"` stops React 19 from preloading every logo in the
    // document <head> (FRIDGE-311): this grid renders below the product grid
    // on collection pages, and each non-lazy <img> in the shell became a
    // `<link rel="preload" as="image">` fetched before first paint (10 of the
    // 56 preloads measured on /collections/french-door-refrigerators,
    // 08/17/2026).
    // FRIDGE-1540: the class height (h-9, h-14, or the caller's) and the width cap
    // are repeated inline, so Chromium counts the lazy logo as sized. object-contain
    // draws the logo in that box exactly as it drew it in its auto-width box.
    // FRIDGE-1547: through the image optimizer, sized to the tile, instead of the
    // stored original (Samsung-Logo-Clean.png is 1,018 px, drawn at 130).
    <img
      ref={ref}
      alt={`${brand.name} Logo`}
      className={logoClass}
      {...optimizedImgSrc(brand.logo, [128, 256, 384])}
      sizes="192px"
      loading="lazy"
      decoding="async"
      style={{ height: logoHeight, width: logoWidth }}
    />
  ) : (
    <span className={`font-bold text-gray-700 group-hover:text-[#ff9951] transition-colors text-center line-clamp-2 ${compact ? "text-xs" : "text-sm"}`}>
      {brand.name}
    </span>
  )
}
